import { useCallback, useEffect, useState } from "react";

import {
  getAllLensHistory,
  getLensHistoryForInteraction,
  LensHistoryItem
} from "../utils/lensHistoryStorage";
import { LENS_NAVIGATION_DIRECTION, LensNavigationDirection } from "../utils/lensNavigation";

const EMPTY_INDEX = -1;
const NEW_VERSION_OFFSET = 0;
const NAVIGATION_STEP = 1;
const DISPLAY_OFFSET = 1;

interface UseLensHistoryForInteractionReturn {
  interactionHistory: LensHistoryItem[];
  allHistory: LensHistoryItem[];
  currentIndex: number;
  canGoBack: boolean;
  canGoForward: boolean;
  currentHistoryItem: LensHistoryItem | null;
  isInNewVersionState: boolean;
  navigateHistory: (direction: LensNavigationDirection) => void;
  navigateToNewVersion: () => void;
  refreshHistory: () => void;
  versionDisplay: string;
}

const getVersionDisplay = (index: number, historyLength: number): string => {
  if (index === historyLength) {
    return "New";
  }
  if (historyLength > NEW_VERSION_OFFSET) {
    return `${index + DISPLAY_OFFSET}/${historyLength}`;
  }
  return "New";
};

const getCurrentItem = (index: number, history: LensHistoryItem[]): LensHistoryItem | null =>
  (index >= NEW_VERSION_OFFSET && index < history.length) ? history[index] : null;

const useLensHistoryForInteraction = (interactionId: string | null): UseLensHistoryForInteractionReturn => {
  const [interactionHistory, setInteractionHistory] = useState<LensHistoryItem[]>([]);
  const [allHistory, setAllHistory] = useState<LensHistoryItem[]>([]);
  const [currentIndex, setCurrentIndex] = useState<number>(EMPTY_INDEX);

  const refreshHistory = useCallback((): void => {
    setAllHistory(getAllLensHistory());
    if (!interactionId) {
      setInteractionHistory([]);
      setCurrentIndex(NEW_VERSION_OFFSET);
      return;
    }
    const filtered = getLensHistoryForInteraction(interactionId);
    setInteractionHistory(filtered);
    setCurrentIndex(filtered.length);
  }, [interactionId]);

  useEffect(() => {
    refreshHistory();
  }, [refreshHistory]);

  const navigateHistory = useCallback((direction: LensNavigationDirection): void => {
    if (direction === LENS_NAVIGATION_DIRECTION.Previous && currentIndex > NEW_VERSION_OFFSET) {
      setCurrentIndex(currentIndex - NAVIGATION_STEP);
      return;
    }
    if (direction === LENS_NAVIGATION_DIRECTION.Next && currentIndex < interactionHistory.length) {
      setCurrentIndex(currentIndex + NAVIGATION_STEP);
    }
  }, [currentIndex, interactionHistory.length]);

  const navigateToNewVersion = useCallback((): void => {
    setCurrentIndex(interactionHistory.length);
  }, [interactionHistory.length]);

  return {
    interactionHistory, allHistory, currentIndex,
    canGoBack: currentIndex > NEW_VERSION_OFFSET,
    canGoForward: currentIndex < interactionHistory.length,
    currentHistoryItem: getCurrentItem(currentIndex, interactionHistory),
    isInNewVersionState: currentIndex === interactionHistory.length,
    navigateHistory, navigateToNewVersion, refreshHistory,
    versionDisplay: getVersionDisplay(currentIndex, interactionHistory.length)
  };
};

export { useLensHistoryForInteraction };
